import React, { useEffect, useState, useMemo } from "react";
import CustomDropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import { useUser } from "src/shared/context/UserContext";
import { getUsersFromIndexedDB } from "src/shared/utils/indexDB/indexedDB";

export interface DropdownUser {
  id: string;
  userName: string;
}

interface UserDropdownProps {
  value?: string;
  onChange?: (user: DropdownUser | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  title?: string;
  titleClassName?: string;
}

const getUserId = (user: Record<string, any>): string => String(user?._id?.$oid || user?._id || user?.id || "");

const getUserName = (user: Record<string, any>): string => String(user?.firstName + " " + user?.middleName + " " + user?.lastName);


/** Displays users saved in IndexedDB, keyed by their Mongo ObjectId using CustomDropdownSelect. */
const UserDropdown: React.FC<UserDropdownProps> = ({ value = "", onChange, placeholder = "All user", disabled = false, className = "", title }) => {
  const [users, setUsers] = useState<DropdownUser[]>([]);
  const { user } = useUser();
  const loginUser = user?.role[0] === "User";

  useEffect(() => {
    if (loginUser) return;
    const loadUsers = async () => {
      try {
        const storedUsers = await getUsersFromIndexedDB();
        const dropdownUsers = storedUsers.filter((orgId) => orgId.orgId === user?.orgId).map((user) => ({ id: getUserId(user), userName: getUserName(user) })).filter((user) => Boolean(user.id));

        setUsers(dropdownUsers);
      } catch (error) {
        console.error("Unable to load users from IndexedDB:", error);
      }
    };

    loadUsers();
  }, [loginUser, user?.orgId]);

  // Map users to match the Option interface expected by CustomDropdownSelect
  const dropdownOptions = useMemo(
    () =>
      users.map((u) => ({
        _id: u.id,
        CategoryName: u.userName,
      })),
    [users],
  );

  const handleChange = (selectedId: string) => {
    const selectedUser = users.find((u) => u.id === selectedId) || null;
    onChange?.(selectedUser);
  };

  return (
    <CustomDropdownSelect
      name="userDropdown"
      value={value}
      options={dropdownOptions}
      onChange={handleChange}
      titleofSelect={placeholder}
      title={title}
      disabled={disabled}
      className={className}
    />
  );
};

export default UserDropdown;
